import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ShieldCheck, Award } from 'lucide-react';
import { COMPANY_INFO, NAVIGATION_MENU } from '../data';

export default function Footer() {
  return (
    <footer className="bg-brand-deep text-white pt-16 pb-8 border-t border-brand-blue/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Bio */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="bg-white p-2 inline-block">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=154,quality=90,format=auto/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/energirude-eksperten-logo.png"
                  alt="Energirude Eksperten logo - Din glarmester"
                  className="h-10 w-auto object-contain block"
                />
              </div>
            </div>
            <p className="text-sm text-brand-light/80 leading-relaxed">
              Din professionelle glarmester i Egedal, Nordsjælland og store dele af Sjælland. Med base i Smørum har vi over 25 års erfaring med udskiftning til moderne energiruder og termoruder med fokus på varmebesparelse og tryghed.
            </p>
            <div className="flex items-center gap-3 pt-2">
              <span className="flex items-center gap-1 text-xs bg-brand-blue/30 text-brand-light px-2.5 py-1 rounded-full border border-brand-blue/40">
                <ShieldCheck className="w-3.5 h-3.5 text-brand-blue" />
                Glarmestergaranti
              </span>
              <span className="flex items-center gap-1 text-xs bg-brand-green/20 text-brand-light px-2.5 py-1 rounded-full border border-brand-green/30">
                <Award className="w-3.5 h-3.5 text-brand-green" />
                5 års garanti
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-base font-semibold text-white uppercase tracking-wider mb-4">
              Hurtige links
            </h3>
            <ul className="space-y-2.5 text-sm text-brand-light/80">
              {NAVIGATION_MENU.map((item, idx) => (
                <li key={idx}>
                  {item.children ? (
                    <span className="font-medium text-white block mb-1">{item.label}</span>
                  ) : (
                    <Link to={item.href} className="hover:text-brand-blue transition-colors">
                      {item.label}
                    </Link>
                  )}
                  {item.children && (
                    <ul className="pl-3 space-y-1.5 border-l border-brand-blue/30 mt-1">
                      {item.children.map((child, cIdx) => (
                        <li key={cIdx}>
                          <Link to={child.href} className="hover:text-brand-blue transition-colors">
                            {child.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-base font-semibold text-white uppercase tracking-wider mb-4">
              Vores Ydelser
            </h3>
            <ul className="space-y-2.5 text-sm text-brand-light/80">
              <li>
                <Link to="/energiruder" className="hover:text-brand-blue transition-colors">
                  Energiruder
                </Link>
              </li>
              <li>
                <Link to="/udskiftning-af-ruder" className="hover:text-brand-blue transition-colors">
                  Udskiftning af ruder
                </Link>
              </li>
              <li>
                <Link to="/termoruder" className="hover:text-brand-blue transition-colors">
                  Termoruder
                </Link>
              </li>
              <li>
                <Link to="/glasloesninger" className="hover:text-brand-blue transition-colors">
                  Glasløsninger
                </Link>
              </li>
              <li>
                <Link to="/gratis-besoeg" className="hover:text-brand-blue transition-colors">
                  Gratis opmåling & rådgivning
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-white uppercase tracking-wider mb-4">
              Kontaktoplysninger
            </h3>
            <ul className="space-y-3 text-sm text-brand-light/80">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-brand-blue shrink-0 mt-0.5" />
                <span className="leading-relaxed">
                  {COMPANY_INFO.address}
                  <span className="block text-xs text-brand-light/60 mt-0.5">
                    Base i Smørum – vi dækker Egedal, Nordsjælland og store dele af Sjælland
                  </span>
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-brand-blue shrink-0" />
                <a href={`tel:${COMPANY_INFO.phone.replace(/\s+/g, '')}`} className="hover:text-brand-blue transition-colors font-semibold">
                  {COMPANY_INFO.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-brand-blue shrink-0" />
                <a href={`mailto:${COMPANY_INFO.email}`} className="hover:text-brand-blue transition-colors">
                  {COMPANY_INFO.email}
                </a>
              </li>
            </ul>
            <div className="pt-2 border-t border-brand-blue/20 text-xs text-brand-light/60">
              <p>CVR: {COMPANY_INFO.cvr}</p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-brand-blue/20 text-center text-xs text-brand-light/60 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>© {new Date().getFullYear()} {COMPANY_INFO.name}. Alle rettigheder forbeholdes.</p>
          <div className="flex gap-6">
            <a href={COMPANY_INFO.trustpilotUrl} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
              Trustpilot ★★★★☆
            </a>
            <Link to="/privatlivspolitik" className="hover:text-white transition-colors">
              Privatlivspolitik
            </Link>
            <Link to="/kontakt" className="hover:text-white transition-colors">
              Kundeservice
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
